import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/components/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Calendar, Package, Truck, Users, TrendingUp, FileText, Printer, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { USER_DIRECTORY_FIELDS, USERS_DIRECTORY_VIEW } from "@/lib/userAccess";
import { isCancelledOrderStatus } from "@/lib/orderStatus";

export function DashboardRepresentante({ codRep }) {
  const { user } = useAuth();
  const [repName, setRepName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [loadingProductDetails, setLoadingProductDetails] = useState(false);
  const [productOrderItems, setProductOrderItems] = useState([]);
  
  // Fechas por defecto (ejercicio actual - año en curso)
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1); // 1 de enero del año actual
  const [fechaDesde, setFechaDesde] = useState(startOfYear.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(today.toISOString().split('T')[0]);
  
  // Estados para los datos
  const [resumen, setResumen] = useState({
    totalPedidos: 0,
    totalPrendas: 0,
    totalVentas: 0,
    totalComision: 0,
    pedidosServidos: 0,
    pedidosPendientes: 0,
    pedidosPreparados: 0,
    pedidosFabricacion: 0
  });
  
  const [pedidos, setPedidos] = useState([]);
  const [productosMasVendidos, setProductosMasVendidos] = useState([]);
  const [activeTab, setActiveTab] = useState('resumen');

  // Estado para dashboard por temporada
  const [temporadas, setTemporadas] = useState([]);
  const [temporadaSeleccionada, setTemporadaSeleccionada] = useState(null);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [seasonError, setSeasonError] = useState(null);
  const [seasonResumen, setSeasonResumen] = useState({
    totalPrendas: 0,
    totalImporte: 0,
    totalComision: 0,
    totalProductos: 0
  });
  const [seasonProductos, setSeasonProductos] = useState([]);

  useEffect(() => {
    console.log('useEffect ejecutado - user:', user?.cod_rep, 'ver_comisiones_globales:', user?.ver_comisiones_globales, 'codRep:', codRep);
    
    if (user) {
      // Cargar nombre del representante
      (async () => {
        try {
          const { data, error } = await supabase
            .from(USERS_DIRECTORY_VIEW)
            .select(USER_DIRECTORY_FIELDS)
            .eq('cod_rep', codRep)
            .limit(1);
          if (!error && data && data.length > 0) {
            setRepName(data[0].name || String(codRep));
          } else {
            setRepName(String(codRep));
          }
        } catch (_) {
          setRepName(String(codRep));
        }
      })();

      if (user.ver_comisiones_globales) {
        // Usuario admin o con permisos globales - puede cargar cualquier dashboard
        console.log('Usuario con permisos globales - cargando dashboard');
        cargarDashboard();
      } else if (user?.cod_rep) {
        // Usuario representante con cod_rep - puede cargar su dashboard
        console.log('Usuario representante - cargando dashboard');
        cargarDashboard();
      } else {
        // Usuario representante sin cod_rep - error
        console.log('Usuario representante sin cod_rep, estableciendo error');
        setError('Usuario no tiene código de representante asignado');
        setLoading(false);
      }
    }
  }, [user, fechaDesde, fechaHasta, codRep]);

  // Cargar temporadas únicas para el selector
  useEffect(() => {
    const fetchTemporadas = async () => {
      try {
        const { data, error } = await supabase
          .from('products')
          .select('temporada')
          .not('temporada', 'is', null)
          .not('temporada', 'eq', '');
        if (error) throw error;
        const uniqueTemporadas = [...new Set((data || []).map((t) => t.temporada))].sort();
        setTemporadas(uniqueTemporadas);
      } catch (e) {
        console.error('Error cargando temporadas:', e);
      }
    };
    fetchTemporadas();
  }, []);

  // Recalcular dashboard por temporada cuando cambien filtros
  useEffect(() => {
    if (temporadaSeleccionada) {
      cargarDashboardTemporada();
    }
  }, [temporadaSeleccionada, fechaDesde, fechaHasta, codRep]);

  const cargarDashboard = async () => {
    try {
      console.log('Iniciando carga del dashboard para codRep:', codRep);
      setLoading(true);
      setError(null);
      
      // Verificar permisos del usuario
      if (user.ver_comisiones_globales) {
        // Usuario admin o con permisos globales - puede ver cualquier dashboard
        console.log('Usuario con permisos globales - accediendo al dashboard');
      } else {
        // Usuario representante - debe tener cod_rep y solo puede ver su propio dashboard
        if (!user?.cod_rep) {
          console.log('Usuario representante sin cod_rep asignado');
          throw new Error('Usuario no tiene código de representante asignado');
        }
        
        if (user.cod_rep !== codRep) {
          console.log('Usuario no tiene permisos para ver este dashboard');
          throw new Error('No tienes permiso para ver esta información');
        }
      }

      console.log('Iniciando carga de datos en paralelo...');
      
      // Cargar todos los datos en paralelo con manejo individual de errores
      const results = await Promise.allSettled([
        cargarResumen(),
        cargarPedidos(),
        cargarProductosMasVendidos()
      ]);
      
      // Verificar si alguna función falló
      const errors = results.filter(result => result.status === 'rejected');
      if (errors.length > 0) {
        console.error('Errores en carga de datos:', errors);
        const errorMessages = errors.map(error => error.reason?.message || 'Error desconocido').join(', ');
        throw new Error(`Errores al cargar datos: ${errorMessages}`);
      }

      console.log('Dashboard cargado exitosamente');

    } catch (error) {
      console.error('Error al cargar dashboard:', error);
      setError(error.message || 'Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  const cargarResumen = async () => {
    try {
      console.log('Iniciando carga de resumen...');
      // Obtener pedidos del representante
      const { data: pedidosData, error: pedidosError } = await supabase
        .from('orders')
        .select(`
          id,
          total,
          status,
          preparation_status,
          cod_rep,
          items:order_items(
            quantity,
            pending_quantity,
            delivered_quantity,
            preparation_quantity
          )
        `)
        .eq('cod_rep', codRep)
        .gte('date', fechaDesde)
        .lte('date', fechaHasta);

      if (pedidosError) throw pedidosError;

      console.log('Resumen: Pedidos obtenidos:', pedidosData?.length || 0);
      const activePedidosData = (pedidosData || []).filter((pedido) => !isCancelledOrderStatus(pedido.status));
      
      // Calcular estadísticas
      let totalPedidos = 0;
      let totalPrendas = 0;
      let totalVentas = 0;
      let pedidosServidos = 0;
      let pedidosPendientes = 0; // por líneas (pending_quantity > 0)
      let pedidosPreparados = 0;
      let pedidosFabricacion = 0;
      let pedidosParcialmenteServidos = 0;
      let pedidosParcialmentePreparados = 0;

      activePedidosData.forEach(pedido => {
        totalPedidos++;
        totalVentas += pedido.total || 0;
        
        // Calcular prendas
        const prendasPedido = pedido.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
        totalPrendas += prendasPedido;

        // Calcular porcentajes de preparación y servicio
        const totalItems = pedido.items?.length || 0;
        const itemsPreparados = pedido.items?.filter(item => item.preparation_quantity > 0).length || 0;
        const itemsServidos = pedido.items?.filter(item => item.delivered_quantity > 0).length || 0;
        const itemsPendientes = pedido.items?.filter(item => item.pending_quantity > 0).length || 0;

        const porcentajePreparacion = totalItems > 0 ? (itemsPreparados / totalItems) * 100 : 0;
        const porcentajeServicio = totalItems > 0 ? (itemsServidos / totalItems) * 100 : 0;

        // Clasificar por estado usando líneas
        if (porcentajeServicio === 100) {
          pedidosServidos++;
        }
        if (itemsPendientes > 0) {
          pedidosPendientes++;
        }

        // Estados de preparación
        if (porcentajePreparacion === 100) {
          pedidosPreparados++;
        } else if (porcentajePreparacion > 0 && porcentajePreparacion < 100) {
          pedidosParcialmentePreparados++;
        }

        // Estados de servicio
        if (porcentajeServicio > 0 && porcentajeServicio < 100) {
          pedidosParcialmenteServidos++;
        }

        // Verificar si hay items pendientes de fabricación
        const tieneFabricacion = pedido.items?.some(item => 
          item.pending_quantity > 0 && item.preparation_quantity === 0
        );
        if (tieneFabricacion) {
          pedidosFabricacion++;
        }
      });

      // Calcular comisión total
      const totalComision = (totalVentas * (user.comision || 0)) / 100;

      setResumen({
        totalPedidos,
        totalPrendas,
        totalVentas,
        totalComision,
        pedidosServidos,
        pedidosPendientes,
        pedidosPreparados,
        pedidosFabricacion,
        pedidosParcialmenteServidos,
        pedidosParcialmentePreparados
      });

      console.log('Resumen cargado exitosamente');

    } catch (error) {
      console.error('Error al cargar resumen:', error);
      throw error;
    }
  };

  const cargarPedidos = async () => {
    try {
      console.log('Iniciando carga de pedidos...');
      const { data: pedidosData, error: pedidosError } = await supabase
        .from('orders')
        .select(`
          id,
          number,
          date,
          total,
          status,
          preparation_status,
          customer:customers(
            id,
            name
          ),
          items:order_items(
            id,
            quantity,
            pending_quantity,
            delivered_quantity,
            preparation_quantity,
            price,
            size,
            color,
            product:products(
              id,
              code,
              description
            )
          )
        `)
        .eq('cod_rep', codRep)
        .gte('date', fechaDesde)
        .lte('date', fechaHasta)
        .order('number', { ascending: false });

      if (pedidosError) throw pedidosError;

      console.log('Pedidos: Datos obtenidos:', pedidosData?.length || 0);
      
      // Calcular comisión y estadísticas por pedido
      const pedidosConComision = (pedidosData || [])
        .filter((pedido) => !isCancelledOrderStatus(pedido.status))
        .map(pedido => {
        const cantidadPrendas = pedido.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
        const comision = (pedido.total * (user.comision || 0)) / 100;
        
        return {
          ...pedido,
          cantidadPrendas,
          comision
        };
      });

      setPedidos(pedidosConComision);

      console.log('Pedidos cargados exitosamente');

    } catch (error) {
      console.error('Error al cargar pedidos:', error);
      throw error;
    }
  };

  const cargarProductosMasVendidos = async () => {
    try {
      console.log('Iniciando carga de productos más vendidos (consulta única)...');
      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          product_id,
          quantity,
          price,
          product:products!inner(
            id,
            code,
            description,
            images,
            main_image_index
          ),
          order:orders!inner(
            id,
            date,
            cod_rep
          )
        `)
        .eq('order.cod_rep', codRep)
        .gte('order.date', fechaDesde)
        .lte('order.date', fechaHasta);

      if (itemsError) throw itemsError;

      const productosMap = new Map();
      for (const item of itemsData || []) {
        const p = item.product;
        if (!p) continue;
        if (!productosMap.has(p.id)) {
          productosMap.set(p.id, {
            id: p.id,
            code: p.code,
            description: p.description,
            images: p.images,
            main_image_index: p.main_image_index,
            cantidadVendida: 0,
            importeTotal: 0,
          });
        }
        const agg = productosMap.get(p.id);
        const cantidad = item.quantity || 0;
        const importe = cantidad * (item.price || 0);
        agg.cantidadVendida += cantidad;
        agg.importeTotal += importe;
      }

      const productosArray = Array.from(productosMap.values()).sort((a, b) => b.cantidadVendida - a.cantidadVendida);
      setProductosMasVendidos(productosArray);
      console.log('Productos más vendidos cargados exitosamente');
    } catch (error) {
      console.error('Error al cargar productos más vendidos:', error);
      throw error;
    }
  };

  // Dashboard por temporada: agrega por artículos de productos con esa temporada
  const cargarDashboardTemporada = async () => {
    try {
      setSeasonLoading(true);
      setSeasonError(null);

      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          id,
          quantity,
          price,
          order:orders!inner(
            id,
            number,
            date,
            cod_rep
          ),
          product:products!inner(
            id,
            code,
            description,
            temporada,
            images,
            main_image_index
          )
        `)
        .eq('order.cod_rep', codRep)
        .gte('order.date', fechaDesde)
        .lte('order.date', fechaHasta)
        .eq('product.temporada', temporadaSeleccionada);

      if (itemsError) throw itemsError;

      const productosMap = new Map();
      let totalPrendas = 0;
      let totalImporte = 0;

      for (const item of itemsData || []) {
        const producto = item.product;
        const key = producto?.id;
        const cantidad = item.quantity || 0;
        const importe = cantidad * (item.price || 0);
        totalPrendas += cantidad;
        totalImporte += importe;
        if (!productosMap.has(key)) {
          productosMap.set(key, {
            id: producto.id,
            code: producto.code,
            description: producto.description,
            images: producto.images,
            main_image_index: producto.main_image_index,
            cantidadVendida: 0,
            importeTotal: 0,
          });
        }
        const agg = productosMap.get(key);
        agg.cantidadVendida += cantidad;
        agg.importeTotal += importe;
      }

      const productos = Array.from(productosMap.values()).sort((a, b) => b.cantidadVendida - a.cantidadVendida);
      const totalComision = (totalImporte * (user.comision || 0)) / 100;

      setSeasonProductos(productos);
      setSeasonResumen({
        totalPrendas,
        totalImporte,
        totalComision,
        totalProductos: productos.length,
      });
    } catch (e) {
      console.error('Error al cargar dashboard por temporada:', e);
      setSeasonError(e.message || 'Error al cargar datos de temporada');
    } finally {
      setSeasonLoading(false);
    }
  };

  const cargarDetallePedido = async (orderId) => {
    try {
      setLoadingDetails(true);
      setSelectedOrder(null);

      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          id,
          number,
          date,
          total,
          status,
          preparation_status,
          customer:customers(
            id,
            name
          ),
          items:order_items(
            id,
            quantity,
            pending_quantity,
            delivered_quantity,
            preparation_quantity,
            preparation_status,
            price,
            size,
            color,
            product:products(
              id,
              code,
              description,
              images,
              main_image_index
            )
          )
        `)
        .eq('id', orderId)
        .eq('cod_rep', codRep)
        .single();

      if (orderError) throw orderError;

      // Calcular comisión
      const comision = (orderData.total * (user.comision || 0)) / 100;
      orderData.comision = comision;

      setSelectedOrder(orderData);
      setShowOrderDetails(true);

    } catch (error) {
      console.error('Error al cargar detalle del pedido:', error);
      setError('Error al cargar el detalle del pedido');
    } finally {
      setLoadingDetails(false);
    }
  };

  const cargarDetalleProducto = async (productId, productCode, productDescription) => {
    try {
      setLoadingProductDetails(true);
      setSelectedProduct({ id: productId, code: productCode, description: productDescription });
      setProductOrderItems([]);

      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          id,
          quantity,
          pending_quantity,
          delivered_quantity,
          preparation_quantity,
          price,
          size,
          color,
          order:orders!inner(
            id,
            number,
            date,
            cod_rep,
            customer:customers(
              id,
              name
            )
          ),
          product:products(
            id,
            code,
            description,
            images,
            main_image_index
          )
        `)
        .eq('product_id', productId)
        .eq('order.cod_rep', codRep)
        .gte('order.date', fechaDesde)
        .lte('order.date', fechaHasta);

      if (itemsError) throw itemsError;

      // Ordenar los datos por número de pedido descendente
      const sortedItems = (itemsData || []).sort((a, b) => {
        const orderA = a.order?.number || 0;
        const orderB = b.order?.number || 0;
        return orderB - orderA; // Orden descendente
      });

      // Debugging: Comparar con el cálculo del dashboard
      const cantidadDetalle = sortedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const importeDetalle = sortedItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0);
      
      // Buscar el producto en el dashboard para comparar
      const productoDashboard = productosMasVendidos.find(p => p.id === productId);
      
      // Solo mostrar logs si hay discrepancia
      if (productoDashboard && (productoDashboard.cantidadVendida !== cantidadDetalle || productoDashboard.importeTotal !== importeDetalle)) {
        console.log('=== DISCREPANCIA DETECTADA ===');
        console.log('Producto:', productCode, productDescription);
        console.log('Dashboard - Cantidad:', productoDashboard.cantidadVendida);
        console.log('Detalle - Cantidad:', cantidadDetalle);
        console.log('Diferencia:', productoDashboard.cantidadVendida - cantidadDetalle);
        console.log('Total líneas en detalle:', sortedItems.length);
        console.log('Total líneas en dashboard:', productoDashboard.items.length);
        
        // Mostrar las primeras 5 líneas del detalle para verificar
        console.log('Primeras 5 líneas del detalle:');
        sortedItems.slice(0, 5).forEach((item, index) => {
          console.log(`${index + 1}. Pedido #${item.order?.number} - Talla: ${item.size}, Color: ${item.color} - Cantidad: ${item.quantity}, Precio: ${item.price}`);
        });
        
        // Mostrar las primeras 5 líneas del dashboard para comparar
        console.log('Primeras 5 líneas del dashboard:');
        productoDashboard.items.slice(0, 5).forEach((item, index) => {
          console.log(`${index + 1}. Pedido #${item.order?.number} - Talla: ${item.size}, Color: ${item.color} - Cantidad: ${item.quantity}, Precio: ${item.price}`);
        });
        
        // Verificar si hay duplicados en el detalle
        const pedidosDetalle = sortedItems.map(item => item.order?.number).sort();
        const pedidosUnicosDetalle = [...new Set(pedidosDetalle)];
        console.log('Pedidos únicos en detalle:', pedidosUnicosDetalle.length);
        console.log('Total pedidos en detalle:', pedidosDetalle.length);
        
        // Verificar combinaciones únicas de talla y color
        const combinacionesDetalle = sortedItems.map(item => `${item.size}-${item.color}`).sort();
        const combinacionesUnicasDetalle = [...new Set(combinacionesDetalle)];
        console.log('Combinaciones únicas (talla-color) en detalle:', combinacionesUnicasDetalle.length);
        console.log('Combinaciones:', combinacionesUnicasDetalle.slice(0, 10));
        
        console.log('==============================');
        
        // Corregir el valor del dashboard con el valor correcto del detalle
        const productosActualizados = productosMasVendidos.map(producto => {
          if (producto.id === productId) {
            return {
              ...producto,
              cantidadVendida: cantidadDetalle,
              importeTotal: importeDetalle,
              items: sortedItems // Actualizar también los items para futuras comparaciones
            };
          }
          return producto;
        });
        
        setProductosMasVendidos(productosActualizados);
        console.log('✅ Dashboard corregido con valores del detalle');
        
        // Sugerir recalcular todo el dashboard
        console.log('💡 Sugerencia: Considera recargar el dashboard para evitar futuras discrepancias');
      }

      setProductOrderItems(sortedItems);
      setShowProductDetails(true);

    } catch (error) {
      console.error('Error al cargar detalle del producto:', error);
      setError('Error al cargar el detalle del producto');
    } finally {
      setLoadingProductDetails(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'partially_served_cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'Pendiente',
      'processing': 'En proceso',
      'completed': 'Completado',
      'cancelled': 'Cancelado',
      'partially_served_cancelled': 'Parcialmente servido cancelado'
    };
    return statusMap[status] || status;
  };

  const getPreparationStatusColor = (status) => {
    const colors = {
      'pending': 'bg-gray-100 text-gray-800',
      'prepared': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPreparationStatusText = (status) => {
    const statusMap = {
      'pending': 'Pendiente',
      'prepared': 'Preparado',
      'completed': 'Completado'
    };
    return statusMap[status] || status;
  };

  const calcularPorcentajesPedido = (pedido) => {
    const totalItems = pedido.items?.length || 0;
    if (totalItems === 0) return { preparacion: 0, servicio: 0, pendiente: 0 };

    const itemsPreparados = pedido.items?.filter(item => item.preparation_quantity > 0).length || 0;
    const itemsServidos = pedido.items?.filter(item => item.delivered_quantity > 0).length || 0;
    const itemsPendientes = pedido.items?.filter(item => item.pending_quantity > 0).length || 0;

    return {
      preparacion: Math.round((itemsPreparados / totalItems) * 100),
      servicio: Math.round((itemsServidos / totalItems) * 100),
      pendiente: Math.round((itemsPendientes / totalItems) * 100)
    };
  };

  const getEstadoDetallado = (pedido) => {
    const porcentajes = calcularPorcentajesPedido(pedido);
    
    if (porcentajes.servicio === 100) return { texto: 'Completado', color: 'bg-green-100 text-green-800' };
    if (porcentajes.servicio > 0 && porcentajes.servicio < 100) return { texto: `Servido ${porcentajes.servicio}%`, color: 'bg-blue-100 text-blue-800' };
    if (porcentajes.preparacion === 100) return { texto: 'Preparado', color: 'bg-yellow-100 text-yellow-800' };
    if (porcentajes.preparacion > 0 && porcentajes.preparacion < 100) return { texto: `Preparado ${porcentajes.preparacion}%`, color: 'bg-orange-100 text-orange-800' };
    if (porcentajes.pendiente > 0) return { texto: 'Pendiente', color: 'bg-gray-100 text-gray-800' };
    
    return { texto: 'Nuevo', color: 'bg-gray-100 text-gray-800' };
  };

  const handleVolver = () => {
    window.location.hash = '#/informes';
  };

  const imprimirResumen = () => {
    const contenido = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h1 style="text-align: center; color: #333;">Dashboard Representante - ${codRep}</h1>
        <p style="text-align: center; color: #666;">Período: ${new Date(fechaDesde).toLocaleDateString('es-ES')} - ${new Date(fechaHasta).toLocaleDateString('es-ES')}</p>
        
        <div style="margin: 20px 0;">
          <h2>Resumen de Estadísticas</h2>
          <table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Total Pedidos</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.totalPedidos}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Total Prendas</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.totalPrendas.toLocaleString('es-ES')}</td>
            </tr>
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Total Ventas</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(resumen.totalVentas)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Comisión</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(resumen.totalComision)}</td>
            </tr>
          </table>
        </div>

        <div style="margin: 20px 0;">
          <h2>Estados de Pedidos</h2>
          <table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Servidos</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.pedidosServidos}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Parcialmente Servidos</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.pedidosParcialmenteServidos}</td>
            </tr>
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Preparados</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.pedidosPreparados}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Parcialmente Preparados</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.pedidosParcialmentePreparados}</td>
            </tr>
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Pendientes</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.pedidosPendientes}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Fabricación</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.pedidosFabricacion}</td>
            </tr>
          </table>
        </div>
      </div>
    `;
    
    const ventana = window.open('', '_blank');
    ventana.document.write(contenido);
    ventana.document.close();
    ventana.print();
  };

  const imprimirPedidos = () => {
    const contenido = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h1 style="text-align: center; color: #333;">Lista de Pedidos - Representante ${codRep}</h1>
        <p style="text-align: center; color: #666;">Período: ${new Date(fechaDesde).toLocaleDateString('es-ES')} - ${new Date(fechaHasta).toLocaleDateString('es-ES')}</p>
        
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <thead>
            <tr style="background-color: #f5f5f5;">
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Pedido</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Cliente</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Fecha</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Prendas</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Importe</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Comisión</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Estado</th>
            </tr>
          </thead>
          <tbody>
            ${pedidos.map(pedido => `
              <tr>
                <td style="padding: 8px; border: 1px solid #ddd;">#${pedido.number}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${pedido.customer?.name || ''}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${new Date(pedido.date).toLocaleDateString('es-ES')}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${pedido.cantidadPrendas}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(pedido.total)}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(pedido.comision)}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${getEstadoDetallado(pedido).texto}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
    
    const ventana = window.open('', '_blank');
    ventana.document.write(contenido);
    ventana.document.close();
    ventana.print();
  };

  const imprimirProductos = () => {
    const contenido = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h1 style="text-align: center; color: #333;">Productos Vendidos - Representante ${codRep}</h1>
        <p style="text-align: center; color: #666;">Período: ${new Date(fechaDesde).toLocaleDateString('es-ES')} - ${new Date(fechaHasta).toLocaleDateString('es-ES')}</p>
        
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <thead>
            <tr style="background-color: #f5f5f5;">
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Código</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Producto</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Cantidad</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Importe</th>
            </tr>
          </thead>
          <tbody>
            ${productosMasVendidos.map(producto => `
              <tr>
                <td style="padding: 8px; border: 1px solid #ddd;">${producto.code}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${producto.description}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${producto.cantidadVendida.toLocaleString('es-ES')}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(producto.importeTotal)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
    
    const ventana = window.open('', '_blank');
    ventana.document.write(contenido);
    ventana.document.close();
    ventana.print();
  };

  const imprimirInformeCompleto = () => {
    const contenido = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h1 style="text-align: center; color: #333;">Informe Completo - Representante ${codRep}</h1>
        <p style="text-align: center; color: #666;">Período: ${new Date(fechaDesde).toLocaleDateString('es-ES')} - ${new Date(fechaHasta).toLocaleDateString('es-ES')}</p>
        
        <div style="page-break-after: always;">
          <h2>Resumen de Estadísticas</h2>
          <table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Total Pedidos</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.totalPedidos}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Total Prendas</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${resumen.totalPrendas.toLocaleString('es-ES')}</td>
            </tr>
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Total Ventas</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(resumen.totalVentas)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>Comisión</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(resumen.totalComision)}</td>
            </tr>
          </table>
        </div>

        <div style="page-break-after: always;">
          <h2>Productos Vendidos (Ordenados por Cantidad)</h2>
          <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
            <thead>
              <tr style="background-color: #f5f5f5;">
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Código</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Producto</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Cantidad</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Importe</th>
              </tr>
            </thead>
            <tbody>
              ${productosMasVendidos.map(producto => `
                <tr>
                  <td style="padding: 8px; border: 1px solid #ddd;">${producto.code}</td>
                  <td style="padding: 8px; border: 1px solid #ddd;">${producto.description}</td>
                  <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${producto.cantidadVendida.toLocaleString('es-ES')}</td>
                  <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(producto.importeTotal)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div>
          <h2>Lista de Pedidos</h2>
          <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
            <thead>
              <tr style="background-color: #f5f5f5;">
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Pedido</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Cliente</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Fecha</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Prendas</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Importe</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Comisión</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Estado</th>
              </tr>
            </thead>
            <tbody>
              ${pedidos.map(pedido => `
                <tr>
                  <td style="padding: 8px; border: 1px solid #ddd;">#${pedido.number}</td>
                  <td style="padding: 8px; border: 1px solid #ddd;">${pedido.customer?.name || ''}</td>
                  <td style="padding: 8px; border: 1px solid #ddd;">${new Date(pedido.date).toLocaleDateString('es-ES')}</td>
                  <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${pedido.cantidadPrendas}</td>
                  <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(pedido.total)}</td>
                  <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(pedido.comision)}</td>
                  <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${getEstadoDetallado(pedido).texto}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
    
    const ventana = window.open('', '_blank');
    ventana.document.write(contenido);
    ventana.document.close();
    ventana.print();
  };

  if (loading) {
    return <div className="p-4">Cargando dashboard...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
                <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          onClick={handleVolver}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <h1 className="text-2xl font-bold">Dashboard Representante - {codRep}</h1>
      </div>
      <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={handleVolver}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold">Dashboard Representante - {repName || codRep}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setActiveTab('temporada')}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Calendar className="h-4 w-4" />
            Ver por temporada
          </Button>
          <Button
            onClick={imprimirResumen}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Resumen
          </Button>
          <Button
            onClick={imprimirInformeCompleto}
            className="flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Informe Completo
          </Button>
        </div>
      </div>

      {/* Filtros de fecha */}
      <Card className="p-4 mb-6">
        <div className="flex gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">Desde</label>
            <Input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hasta</label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {/* Resumen de estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Total Pedidos</h3>
          </div>
          <p className="text-3xl font-bold">{resumen.totalPedidos}</p>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold">Total Prendas</h3>
          </div>
          <p className="text-3xl font-bold">{resumen.totalPrendas.toLocaleString('es-ES')}</p>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-semibold">Total Ventas</h3>
          </div>
          <p className="text-3xl font-bold">
            {new Intl.NumberFormat('es-ES', {
              style: 'currency',
              currency: 'EUR'
            }).format(resumen.totalVentas)}
          </p>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold">Comisión</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">
            {new Intl.NumberFormat('es-ES', {
              style: 'currency',
              currency: 'EUR'
            }).format(resumen.totalComision)}
          </p>
        </Card>
      </div>

      {/* Estados de pedidos (simplificado) */}
      <TooltipProvider>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold">Servidos</h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>Pedidos con servicio 100% por líneas</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-bold text-green-600">{resumen.pedidosServidos}</p>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold">Pendientes</h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>Pedidos con al menos una línea con pending_quantity &gt; 0</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-bold text-gray-600">{resumen.pedidosPendientes}</p>
        </Card>
      </div>
      </TooltipProvider>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="resumen" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Resumen
          </TabsTrigger>
          <TabsTrigger value="pedidos" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Pedidos
          </TabsTrigger>
          <TabsTrigger value="detalle" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Informe Detallado
          </TabsTrigger>
          <TabsTrigger value="temporada" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Temporada
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Tabla de pedidos */}
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4">Pedidos de Clientes</h2>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pedido</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Prendas</TableHead>
                      <TableHead className="text-right">Importe</TableHead>
                      <TableHead className="text-right">Comisión</TableHead>
                      <TableHead className="text-center">Preparación</TableHead>
                      <TableHead className="text-center">Servicio</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead className="text-center">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pedidos.map((pedido) => (
                      <TableRow key={pedido.id}>
                        <TableCell className="font-medium">#{pedido.number}</TableCell>
                        <TableCell>{pedido.customer?.name}</TableCell>
                        <TableCell>
                          {new Date(pedido.date).toLocaleDateString('es-ES')}
                        </TableCell>
                        <TableCell className="text-right">
                          {pedido.cantidadPrendas}
                        </TableCell>
                        <TableCell className="text-right">
                          {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(pedido.total)}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(pedido.comision)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center">
                            <span className="text-sm font-medium">{calcularPorcentajesPedido(pedido).preparacion}%</span>
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full" 
                                style={{ width: `${calcularPorcentajesPedido(pedido).preparacion}%` }}
                              ></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center">
                            <span className="text-sm font-medium">{calcularPorcentajesPedido(pedido).servicio}%</span>
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-600 h-2 rounded-full" 
                                style={{ width: `${calcularPorcentajesPedido(pedido).servicio}%` }}
                              ></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={getEstadoDetallado(pedido).color}>
                            {getEstadoDetallado(pedido).texto}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => cargarDetallePedido(pedido.id)}
                            disabled={loadingDetails}
                          >
                            Ver Detalles
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>

            {/* Productos más vendidos */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Productos Vendidos (Ordenados por Cantidad)</h2>
                <Button
                  onClick={imprimirProductos}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  Imprimir Productos
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead></TableHead>
                      <TableHead>Código</TableHead>
                      <TableHead>Producto</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Importe</TableHead>
                      <TableHead className="text-center">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productosMasVendidos.map((producto) => (
                      <TableRow key={producto.id}>
                        <TableCell className="w-[56px]">
                          <div className="w-12 h-12 rounded overflow-hidden bg-gray-100">
                            <img
                              src={producto.images?.[producto.main_image_index] || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="%23e5e7eb"><rect width="24" height="24" rx="4"/><path d="M7 17l3-3 2 2 4-4 3 3v2H7z" fill="%23cbd5e1"/></svg>'}
                              alt={producto.description}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{producto.code}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {producto.description}
                        </TableCell>
                        <TableCell className="text-right">
                          {producto.cantidadVendida.toLocaleString('es-ES')}
                        </TableCell>
                        <TableCell className="text-right">
                          {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(producto.importeTotal)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => cargarDetalleProducto(producto.id, producto.code, producto.description)}
                            disabled={loadingProductDetails}
                          >
                            Ver Detalles
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Dashboard por Temporada */}
        <TabsContent value="temporada" className="space-y-6">
          <Card className="p-6">
            <div className="flex flex-wrap items-end gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">Temporada</label>
                <Select value={temporadaSeleccionada || ""} onValueChange={(v) => setTemporadaSeleccionada(v || null)}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Selecciona temporada" />
                  </SelectTrigger>
                  <SelectContent>
                    {temporadas.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Acciones</label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => temporadaSeleccionada && cargarDashboardTemporada()}
                    disabled={!temporadaSeleccionada || seasonLoading}
                    className="flex items-center gap-2"
                  >
                    🔄 Recalcular
                  </Button>
                </div>
              </div>
            </div>

            {seasonError && (
              <div className="text-red-600 text-sm mb-4">{seasonError}</div>
            )}

            {!temporadaSeleccionada ? (
              <div className="text-sm text-gray-600">Selecciona una temporada para ver los datos.</div>
            ) : seasonLoading ? (
              <div className="text-sm">Cargando datos de temporada...</div>
            ) : (
              <div className="space-y-6">
                {/* Resumen temporada */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-5 w-5 text-green-600" />
                      <h3 className="text-lg font-semibold">Prendas</h3>
                    </div>
                    <p className="text-3xl font-bold">{seasonResumen.totalPrendas.toLocaleString('es-ES')}</p>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                      <h3 className="text-lg font-semibold">Importe</h3>
                    </div>
                    <p className="text-3xl font-bold">{new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(seasonResumen.totalImporte)}</p>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      <h3 className="text-lg font-semibold">Comisión</h3>
                    </div>
                    <p className="text-3xl font-bold text-green-600">{new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(seasonResumen.totalComision)}</p>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold">Productos</h3>
                    </div>
                    <p className="text-3xl font-bold">{seasonResumen.totalProductos}</p>
                  </Card>
                </div>

                {/* Tabla productos por temporada */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead></TableHead>
                        <TableHead>Código</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead className="text-right">Cantidad</TableHead>
                        <TableHead className="text-right">Importe</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {seasonProductos.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="w-[56px]">
                            <div className="w-12 h-12 rounded overflow-hidden bg-gray-100">
                              <img
                                src={p.images?.[p.main_image_index] || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="%23e5e7eb"><rect width="24" height="24" rx="4"/><path d="M7 17l3-3 2 2 4-4 3 3v2H7z" fill="%23cbd5e1"/></svg>'}
                                alt={p.description}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{p.code}</TableCell>
                          <TableCell className="max-w-[280px] truncate">{p.description}</TableCell>
                          <TableCell className="text-right">{p.cantidadVendida.toLocaleString('es-ES')}</TableCell>
                          <TableCell className="text-right">{new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(p.importeTotal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="pedidos" className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Lista Completa de Pedidos</h2>
              <Button
                onClick={imprimirPedidos}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Imprimir Pedidos
              </Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pedido</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Prendas</TableHead>
                    <TableHead className="text-right">Importe</TableHead>
                    <TableHead className="text-right">Comisión</TableHead>
                    <TableHead className="text-center">Preparación</TableHead>
                    <TableHead className="text-center">Servicio</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pedidos.map((pedido) => (
                    <TableRow key={pedido.id}>
                      <TableCell className="font-medium">#{pedido.number}</TableCell>
                      <TableCell>{pedido.customer?.name}</TableCell>
                      <TableCell>
                        {new Date(pedido.date).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell className="text-right">
                        {pedido.cantidadPrendas}
                      </TableCell>
                      <TableCell className="text-right">
                        {new Intl.NumberFormat('es-ES', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(pedido.total)}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {new Intl.NumberFormat('es-ES', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(pedido.comision)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-sm font-medium">{calcularPorcentajesPedido(pedido).preparacion}%</span>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${calcularPorcentajesPedido(pedido).preparacion}%` }}
                            ></div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-sm font-medium">{calcularPorcentajesPedido(pedido).servicio}%</span>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ width: `${calcularPorcentajesPedido(pedido).servicio}%` }}
                            ></div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={getEstadoDetallado(pedido).color}>
                          {getEstadoDetallado(pedido).texto}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cargarDetallePedido(pedido.id)}
                          disabled={loadingDetails}
                        >
                          Ver Detalles
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="detalle" className="space-y-6">
          <Card className="p-6">
            <h2 className="text-xl font-bold mb-4">Informe Detallado de Comisiones y Prendas</h2>
            <div className="space-y-6">
              {pedidos.map((pedido) => (
                <div key={pedido.id} className="border rounded-lg p-4 space-y-4 bg-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold">Pedido #{pedido.number}</h3>
                      <p className="text-sm text-gray-500">
                        {new Date(pedido.date).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={getEstadoDetallado(pedido).color}>
                        {getEstadoDetallado(pedido).texto}
                      </Badge>
                      <div className="flex flex-col items-end">
                        <span className="font-semibold">
                          {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(pedido.total)}
                        </span>
                        <span className="text-sm text-green-600">
                          Comisión: {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(pedido.comision)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-sm">
                    <p className="font-medium">Cliente: {pedido.customer?.name}</p>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">Código</TableHead>
                          <TableHead className="whitespace-nowrap">Producto</TableHead>
                          <TableHead className="whitespace-nowrap">Talla</TableHead>
                          <TableHead className="whitespace-nowrap">Color</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Cantidad</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Precio</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pedido.items?.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="whitespace-nowrap">{item.product?.code}</TableCell>
                            <TableCell className="max-w-[200px] truncate">{item.product?.description}</TableCell>
                            <TableCell className="whitespace-nowrap">{item.size}</TableCell>
                            <TableCell className="whitespace-nowrap">{item.color}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{item.quantity}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {new Intl.NumberFormat('es-ES', {
                                style: 'currency',
                                currency: 'EUR'
                              }).format(item.price)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {new Intl.NumberFormat('es-ES', {
                                style: 'currency',
                                currency: 'EUR'
                              }).format(item.price * item.quantity)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de detalles del pedido */}
      <Dialog open={showOrderDetails} onOpenChange={() => setShowOrderDetails(false)}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Detalle del Pedido #{selectedOrder?.number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="flex-1 overflow-y-auto pr-2">
              <div className="space-y-6">
                <div className="border rounded-lg p-4 space-y-4 bg-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold">Pedido #{selectedOrder.number}</h3>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedOrder.date).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={getStatusColor(selectedOrder.status)}>
                        {getStatusText(selectedOrder.status)}
                      </Badge>
                      <Badge className={getPreparationStatusColor(selectedOrder.preparation_status)}>
                        {getPreparationStatusText(selectedOrder.preparation_status)}
                      </Badge>
                      <div className="flex flex-col items-end">
                        <span className="font-semibold">
                          {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(selectedOrder.total)}
                        </span>
                        <span className="text-sm text-green-600">
                          Comisión: {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(selectedOrder.comision)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-sm">
                    <p className="font-medium">Cliente: {selectedOrder.customer?.name}</p>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">Código</TableHead>
                          <TableHead className="whitespace-nowrap">Producto</TableHead>
                          <TableHead className="whitespace-nowrap">Talla</TableHead>
                          <TableHead className="whitespace-nowrap">Color</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Cantidad</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Pendiente</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Servido</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Preparado</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Precio</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedOrder.items?.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="w-[56px]">
                              <div className="w-12 h-12 rounded overflow-hidden bg-gray-100">
                                <img
                                  src={item.product?.images?.[item.product?.main_image_index] || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="%23e5e7eb"><rect width="24" height="24" rx="4"/><path d="M7 17l3-3 2 2 4-4 3 3v2H7z" fill="%23cbd5e1"/></svg>'}
                                  alt={item.product?.description}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{item.product?.code}</TableCell>
                            <TableCell className="max-w-[200px] truncate">{item.product?.description}</TableCell>
                            <TableCell className="whitespace-nowrap">{item.size}</TableCell>
                            <TableCell className="whitespace-nowrap">{item.color}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{item.quantity}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{item.pending_quantity}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{item.delivered_quantity}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{item.preparation_quantity}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {new Intl.NumberFormat('es-ES', {
                                style: 'currency',
                                currency: 'EUR'
                              }).format(item.price)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {new Intl.NumberFormat('es-ES', {
                                style: 'currency',
                                currency: 'EUR'
                              }).format(item.price * item.quantity)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de detalles del producto */}
      <Dialog open={showProductDetails} onOpenChange={() => setShowProductDetails(false)}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Detalle del Producto: {selectedProduct?.code} - {selectedProduct?.description}
            </DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="flex-1 overflow-y-auto pr-2">
              <div className="space-y-6">
                <div className="border rounded-lg p-4 space-y-4 bg-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold">Producto: {selectedProduct.code}</h3>
                      <p className="text-sm text-gray-500">{selectedProduct.description}</p>
                      <p className="text-sm text-gray-500">
                        Período: {new Date(fechaDesde).toLocaleDateString('es-ES')} - {new Date(fechaHasta).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="font-semibold">
                        Total Líneas: {productOrderItems.length}
                      </span>
                      <span className="text-sm text-green-600">
                        Cantidad Total: {productOrderItems.reduce((sum, item) => sum + (item.quantity || 0), 0).toLocaleString('es-ES')}
                      </span>
                      <span className="text-sm text-blue-600">
                        Importe Total: {new Intl.NumberFormat('es-ES', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(productOrderItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0))}
                      </span>
                      {/* Comparación con dashboard */}
                      {(() => {
                        const productoDashboard = productosMasVendidos.find(p => p.id === selectedProduct.id);
                        const cantidadDetalle = productOrderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
                        const importeDetalle = productOrderItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0);
                        const diffCantidad = (productoDashboard?.cantidadVendida || 0) - cantidadDetalle;
                        const diffImporte = (productoDashboard?.importeTotal || 0) - importeDetalle;
                        
                        if (diffCantidad !== 0 || diffImporte !== 0) {
                          return (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                              <div className="text-yellow-800 font-medium">⚠️ Discrepancia detectada:</div>
                              <div className="text-yellow-700">
                                Dashboard: {productoDashboard?.cantidadVendida?.toLocaleString('es-ES') || 0} uds
                              </div>
                              <div className="text-yellow-700">
                                Detalle: {cantidadDetalle.toLocaleString('es-ES')} uds
                              </div>
                              <div className="text-yellow-700">
                                Diferencia: {diffCantidad > 0 ? '+' : ''}{diffCantidad.toLocaleString('es-ES')} uds
                              </div>
                              <div className="mt-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => cargarDashboard()}
                                  className="text-xs"
                                >
                                  🔄 Recalcular Dashboard
                                </Button>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">Pedido</TableHead>
                          <TableHead className="whitespace-nowrap">Cliente</TableHead>
                          <TableHead className="whitespace-nowrap">Fecha</TableHead>
                          <TableHead className="whitespace-nowrap">Talla</TableHead>
                          <TableHead className="whitespace-nowrap">Color</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Cantidad</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Pendiente</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Servido</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Preparado</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Precio</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {productOrderItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="w-[56px]">
                              <div className="w-12 h-12 rounded overflow-hidden bg-gray-100">
                                <img
                                  src={item.product?.images?.[item.product?.main_image_index] || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="%23e5e7eb"><rect width="24" height="24" rx="4"/><path d="M7 17l3-3 2 2 4-4 3 3v2H7z" fill="%23cbd5e1"/></svg>'}
                                  alt={item.product?.description}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap font-medium">
                              #{item.order?.number}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {item.order?.customer?.name}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {new Date(item.order?.date).toLocaleDateString('es-ES')}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{item.size}</TableCell>
                            <TableCell className="whitespace-nowrap">{item.color}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{item.quantity}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{item.pending_quantity}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{item.delivered_quantity}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{item.preparation_quantity}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {new Intl.NumberFormat('es-ES', {
                                style: 'currency',
                                currency: 'EUR'
                              }).format(item.price)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {new Intl.NumberFormat('es-ES', {
                                style: 'currency',
                                currency: 'EUR'
                              }).format(item.price * item.quantity)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
} 
